import { FC, useEffect, useMemo, useRef, useState } from 'react';

// material-ui
import { styled, useTheme } from '@mui/material/styles';
import {
    Avatar as MuiAvatar,
    Badge,
    Box as MuiBox,
    Chip,
    ClickAwayListener,
    Grid,
    Paper,
    Popper,
    Stack,
    Typography,
    useMediaQuery,
    Divider,
    CardActions,
    Button
} from '@mui/material';

// third-party
import PerfectScrollbar from 'react-perfect-scrollbar';

// project imports
import MainCard from 'ui-component/cards/MainCard';
import Transitions from 'ui-component/extended/Transitions';
import NotificationList from './NotificationList';

// assets
import { IconBell } from '@tabler/icons';
import usePolling from 'hooks/usePolling';
import { useTranslation } from 'react-i18next';
import NotificationService from 'services/NotificationService';
import useAuth from 'hooks/useAuth';
import Notification from 'models/results/notification';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'store';
import { setIsOpen } from 'store/slices/notification';
import AlertProgressPopper from './AlertProgressPopper';

// ==============================|| STYLES ||============================== //

const IconContainer = styled(MuiBox)(({ theme }) => ({
    marginLeft: '16px',
    marginRight: '24px',
    position: 'relative',
    [theme.breakpoints.down('md')]: {
        marginRight: '16px'
    }
}));

const Loader = styled('div')(({ theme }) => ({
    position: 'absolute',
    bottom: 0,
    left: 0,
    width: '100%',
    height: '3px',
    'border-bottom-left-radius': '16px',
    'border-bottom-right-radius': '16px',
    '--c': `no-repeat linear-gradient(${theme.palette.primary.dark} 0 0)`,
    background: `var(--c), var(--c), ${theme.palette.primary.light}`,
    backgroundSize: '60% 100%',
    animation: 'l16 2s infinite',
    '@keyframes l16': {
        '0%': { backgroundPosition: '-150% 0, -150% 0' },
        '66%': { backgroundPosition: '250% 0, -150% 0' },
        '100%': { backgroundPosition: '250% 0, 250% 0' }
    }
}));

const Avatar = styled(MuiAvatar)(({ theme }) => ({
    ...theme.typography.commonAvatar,
    ...theme.typography.mediumAvatar,
    transition: 'all .2s ease-in-out',
    background: theme.palette.mode === 'dark' ? theme.palette.dark.main : theme.palette.secondary.light,
    color: theme.palette.mode === 'dark' ? theme.palette.warning.dark : theme.palette.secondary.dark,
    '&[aria-controls="menu-list-grow"],&:hover': {
        background: theme.palette.mode === 'dark' ? theme.palette.warning.dark : theme.palette.secondary.dark,
        color: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.secondary.light
    }
}));

// ==============================|| NOTIFICATION ||============================== //

const NotificationSection: FC = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { user } = useAuth();
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const matchesXs = useMediaQuery(theme.breakpoints.down('md'));

    const { isOpen, isProgress } = useSelector((state) => state.notification);

    const [notifications, setNotifications] = useState<Notification[]>([]);
    const anchorRef = useRef<any>(null);

    const notificationService = new NotificationService();

    const handleToggle = () => {
        dispatch(setIsOpen(true));
    };

    const handleClose = (event: React.MouseEvent<HTMLDivElement> | MouseEvent | TouchEvent) => {
        if (anchorRef.current && anchorRef.current.contains(event.target)) {
            return;
        }
        dispatch(setIsOpen(false));
    };

    const handleViewAll = () => {
        navigate('/params-history');
        dispatch(setIsOpen(false));
    };

    const getNotifications = async () => {
        if (!user) return;

        try {
            const res = await notificationService.getNotifications(user.tenantId);
            setNotifications(res);
        } catch (error) {
            console.log(error);
        }
    };

    const isInProgress = useMemo(() => isProgress || notifications.some((n) => n.notificationStatus === 0), [notifications, isProgress]);

    usePolling(async () => {
        getNotifications();
    }, 20000);

    useEffect(() => {
        if (isOpen) {
            getNotifications();
        }
    }, [isOpen]);

    return (
        <>
            <IconContainer>
                <Badge badgeContent={notifications.length} color="primary">
                    <Avatar
                        variant="rounded"
                        ref={anchorRef}
                        aria-controls={isOpen ? 'notification-list' : undefined}
                        aria-haspopup="true"
                        onClick={handleToggle}
                        color="inherit"
                    >
                        <IconBell stroke={1.5} size="1.3rem" />
                    </Avatar>
                </Badge>
                {isInProgress && <Loader />}
            </IconContainer>

            <Popper
                placement={matchesXs ? 'bottom' : 'bottom-end'}
                open={isOpen}
                anchorEl={anchorRef.current}
                transition
                disablePortal
                popperOptions={{
                    modifiers: [
                        {
                            name: 'offset',
                            options: {
                                offset: [matchesXs ? 5 : 0, 20]
                            }
                        }
                    ]
                }}
            >
                {({ TransitionProps }) => (
                    <ClickAwayListener onClickAway={handleClose}>
                        <Transitions position={matchesXs ? 'top' : 'top-right'} in={isOpen} {...TransitionProps}>
                            <Paper>
                                {isOpen && (
                                    <MainCard border={false} elevation={16} content={false} boxShadow shadow={theme.shadows[16]}>
                                        <Grid container direction="column" spacing={2} sx={{ pt: 2, px: 2 }}>
                                            <Grid item xs={12}>
                                                <Stack direction="row" spacing={2}>
                                                    <Typography variant="subtitle1">{t('ALL_NOTIFICATIONS')}</Typography>
                                                    <Chip
                                                        size="small"
                                                        label={notifications.length}
                                                        sx={{
                                                            color: theme.palette.background.default,
                                                            bgcolor: theme.palette.warning.dark
                                                        }}
                                                    />
                                                </Stack>
                                            </Grid>
                                            <Grid item xs={12} sx={{ mb: 2 }}>
                                                <PerfectScrollbar
                                                    style={{ height: '100%', maxHeight: 'calc(100vh - 205px)', overflowX: 'hidden' }}
                                                >
                                                    <NotificationList notifications={notifications} />
                                                </PerfectScrollbar>
                                            </Grid>
                                        </Grid>
                                        <Divider />
                                        <CardActions sx={{ p: 1.25, justifyContent: 'center' }}>
                                            <Button size="small" disableElevation onClick={handleViewAll}>
                                                {t('VIEW_ALL')}
                                            </Button>
                                        </CardActions>
                                    </MainCard>
                                )}
                            </Paper>
                        </Transitions>
                    </ClickAwayListener>
                )}
            </Popper>

            <AlertProgressPopper open anchorEl={anchorRef.current} />
        </>
    );
};

export default NotificationSection;
